<template>
  <main class="main main--has-floating-buttons">
    <template v-if="!isEmpty(ratePlan)">
      <portal to="headerTitle">
        {{ ratePlan.name[hotelSettings.details.primaryLanguage] }}
      </portal>
      <tabable-card id="primary" cols="12 6@desktop">
        <tabable-card-content :id="hotelSettings.details.primaryLanguage" :title="$t(`lang.${hotelSettings.details.primaryLanguage}`)">
          <hm-card-inner>
            <hm-input v-model="ratePlan.name[hotelSettings.details.primaryLanguage]"
                      :lazy="true"
                      :disabled="ratePlan.deleted"
                      :label="$t('admin.pageRatePlan.title')"
                      validate="required"
                      name="title"
                      cols="12" />
            <hm-input v-model="ratePlan.internalName[hotelSettings.details.primaryLanguage]"
                      :lazy="true"
                      :disabled="ratePlan.deleted"
                      :label="$t('admin.pageRatePlan.internalName')"
                      :help="$t('admin.pageRatePlan.internalName_help')"
                      name="internalName"
                      cols="12" />
            <hm-textarea v-model="ratePlan.description[hotelSettings.details.primaryLanguage]"
                         :lazy="true"
                         :disabled="ratePlan.deleted"
                         :label="$t('admin.pageRatePlan.description')"
                         name="description"
                         cols="12" />
            <hm-col v-if="ratePlan.type === 'Package'" cols="12">
              <ckeditor-simple v-model="ratePlan.longDescription[hotelSettings.details.primaryLanguage]" :disabled="ratePlan.deleted" :title="$t('admin.pageRatePlan.longDescription')" />
            </hm-col>
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>
      <tabable-card id="translate" cols="12 6@desktop">
        <tabable-card-content v-for="lang in hotelSettings.details.additionalLanguages" :id="lang" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner>
            <hm-input v-model="ratePlan.name[lang]"
                      :lazy="true"
                      :disabled="ratePlan.deleted"
                      :label="$t('admin.pageRatePlan.title')"
                      name="title"
                      cols="12" />
            <hm-input v-model="ratePlan.internalName[lang]"
                      :lazy="true"
                      :disabled="ratePlan.deleted"
                      :label="$t('admin.pageRatePlan.internalName')"
                      name="internalName"
                      cols="12" />
            <hm-textarea v-model="ratePlan.description[lang]"
                         :lazy="true"
                         :disabled="ratePlan.deleted"
                         :label="$t('admin.pageRatePlan.description')"
                         name="description"
                         cols="12" />
            <hm-col v-if="ratePlan.type === 'Package'" cols="12">
              <ckeditor-simple v-model="ratePlan.longDescription[lang]" :disabled="ratePlan.deleted" :title="$t('admin.pageRatePlan.longDescription')" />
            </hm-col>
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>

      <card v-if="ratePlan.ratePlanMode === 'Derived'" :title="$t('admin.pageRatePlan.card.derivedRate')" cols="12">
        <hm-input-select v-model="ratePlan.parentRatePlanId"
                         :disabled="ratePlan.deleted"
                         :label="$t('admin.pageRatePlan.parentRatePlanId')"
                         name="parentRatePlanId"
                         cols="12 6@tablet 3@desktop">
          <option v-for="item in parentRatePlans"
                  v-if="item.ratePlanType === ratePlan.type"
                  :key="item.id"
                  :value="item.id"
                  :disabled="item.deleted || (ratePlan.rateEntryMode !== item.rateEntryMode) || (ratePlan.rateEntryMode === 'Daily' && !isEmpty(item.parentRatePlanId))"
                  v-html="item.name[$shortLocale]"></option>
        </hm-input-select>
        <hm-multiselect v-if="ratePlan.rateEntryMode === 'Seasonal'"
                        id="inheritRestrictions"
                        v-model="ratePlan.inheritRestrictions"
                        :options="restrictions"
                        :disabled="ratePlan.deleted"
                        :label="$t('admin.pageRatePlan.inheritRestrictions')"
                        cols="12 6@tablet 3@desktop"
                        name="inheritRestrictions"
                        label-expression="${option.name}" />
        <hm-input-toggle v-if="ratePlan.rateEntryMode === 'Seasonal'"
                         v-model="ratePlan.useSimpleRelativeRateAdjustment"
                         :disabled="ratePlan.deleted"
                         :label="$t('admin.pageRatePlan.useSimpleRelativeRateAdjustment')"
                         :help="$t('admin.pageRatePlan.useSimpleRelativeRateAdjustment_help')"
                         name="useSimpleRelativeRateAdjustment"
                         cols="12 6@tablet 3@desktop start-1" />
        <template v-if="ratePlan.useSimpleRelativeRateAdjustment">
          <hm-input-money v-model="ratePlan.simpleRelativeRateAdjustmentAmount"
                          :disabled="ratePlan.deleted"
                          :currency="`${ratePlan.simpleRelativeRateAdjustmentPercentage ? '%' : hotelSettings.details.currency}`"
                          :digits="ratePlan.simpleRelativeRateAdjustmentPercentage ? 5 : 2"
                          :lazy="true"
                          :currency-clickable="true"
                          :label="$t('admin.pageRatePlan.simpleRelativeRateAdjustmentAmount')"
                          name="simpleRelativeRateAdjustmentAmount"
                          cols="12 6@tablet 3@desktop"
                          @currency-click="ratePlan.simpleRelativeRateAdjustmentPercentage = !ratePlan.simpleRelativeRateAdjustmentPercentage">
            <template slot="icon-inline-left">
              <input-icon-button :border="false" icon="plus" />
            </template>
          </hm-input-money>
        </template>
      </card>

      <card id="general" :title="$t('admin.pageRatePlan.card.general')" cols="12 6@desktop" rows="2">
        <hm-card-inner v-if="ratePlan.type === 'Package' && ratePlan.rateEntryMode !== 'Daily' && isEmpty(ratePlan.validityDateRanges)" :full-size="true">
          <hm-col cols="12">
            <span class="warning-text warning-text--full-size warning-text--no-margin col-12">{{ $t('admin.pageRatePlan.warningNoValidityDateRanges') }}</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner id="ratePlanOptions">
          <hm-input v-model="ratePlan.id"
                    :disabled="true"
                    :label="$t('admin.pageRatePlan.id')"
                    name="id"
                    cols="12" />
          <hm-multiselect id="roomTypes"
                          v-model="ratePlan.applicableRoomTypes"
                          :options="hotelSettings.roomTypes.all(ratePlan.applicableRoomTypes).filter(it => isEmpty(ratePlan.parentRatePlanId) || findHotelSettingById('ratePlans', ratePlan.parentRatePlanId).applicableRoomTypes.includes(it.id))"
                          :disabled="ratePlan.deleted"
                          :label="$t('admin.pageRatePlan.applicableRoomTypes')"
                          cols="12 6@tablet start-1"
                          name="applicableRoomTypes"
                          label-expression="${option.name[$shortLocale]}" />
          <hm-input-select v-model="ratePlan.includedMealPlan"
                           :disabled="ratePlan.deleted"
                           :label="$t('admin.pageRatePlan.includedMealPlan')"
                           name="includedMealPlan"
                           cols="12 6@tablet start-1">
            <option :value="null"></option>
            <option v-for="item in hotelSettings.mealPlans.all(ratePlan.includedMealPlan)" :key="item.id" :value="item.id" v-html="item.name[$shortLocale]"></option>

            <template slot="icon-inline-right" slot-scope="slot">
              <input-icon-button :border="true"
                                 :disabled="isEmpty(ratePlan.includedMealPlan) || ratePlan.deleted"
                                 icon="edit"
                                 @click="$router.push({ name: 'mealPlan', params: { mealPlanId: ratePlan.includedMealPlan }})"/>
            </template>
          </hm-input-select>
          <hm-input-select v-model="ratePlan.reservationStatus"
                           :disabled="ratePlan.deleted"
                           :label="$t('admin.pageRatePlan.reservationStatus')"
                           :help="$t('admin.pageRatePlan.reservationStatus_help')"
                           name="reservationStatus"
                           cols="12 6@tablet start-1">
            <option :value="null"></option>
            <option value="Guaranteed" v-html="$t(`ReservationStatus.Guaranteed`)"></option>
            <option value="NonGuaranteed" v-html="$t(`ReservationStatus.NonGuaranteed`)"></option>
          </hm-input-select>
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageRatePlan.card.displayOptions')">
          <template v-if="hotelSettings.details.google">
            <hm-col cols="12">
              <span class="info-text col-12">{{ $t('admin.pageRatePlan.infoTextGoogle') }}</span>
            </hm-col>
          </template>
          <hm-input-toggle v-model="ratePlan.publicVisible"
                           :disabled="ratePlan.deleted || (!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && hotelSettings.details.google && ratePlan.google && ratePlan.publicVisible)"
                           :label="$t('admin.pageRatePlan.publicVisible')"
                           name="publicVisbile"
                           cols="12 6@tablet start-1" />
          <hm-input-toggle v-if="ratePlan.type !== 'Package'"
                           v-model="ratePlan.hideInternally"
                           :disabled="ratePlan.deleted"
                           :label="$t('admin.pageRatePlan.hideInternally')"
                           name="hideInternally"
                           cols="12 6@tablet start-1" />
          <template v-if="ratePlan.type === 'DayRate'">
            <hm-input-select v-model="ratePlan.ratePostingMode"
                             :disabled="ratePlan.deleted"
                             :label="$t('admin.pageRatePlan.ratePostingMode')"
                             :help="$t('admin.pageRatePlan.ratePostingMode_help')"
                             name="ratePostingMode"
                             enum-name="RatePostingMode"
                             cols="12 6@tablet start-1" />
          </template>
        </hm-card-inner>
        <hm-card-inner v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') || hasRole('ROLE_SYSADMIN')" :title="$t('admin.pageRatePlan.card.adminOptions')">

          <hm-input-select v-model="ratePlan.rateEntryMode"
                           :disabled="ratePlan.deleted || !hasLicense('DailyRateEntry')"
                           :label="$t('admin.pageRatePlan.rateEntryMode')"
                           name="rateEntryMode"
                           enum-name="RateEntryMode"
                           cols="12 6@tablet start-1" />
          <hm-input-toggle v-model="ratePlan.google"
                           :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           name="google"
                           cols="12 6@tablet start-1"
                           label="Google?"/>
          <hm-input-toggle v-if="hasRole('ROLE_SYSADMIN') && !ratePlan.deleted && ratePlanLevel === 0 && ratePlan.type === 'DayRate'"
                           v-model="ratePlan.defaultRatePlan"
                           :disabled="ratePlan.deleted || ratePlan.defaultRatePlan"
                           name="defaultRatePlan"
                           label="Default Rate Plan?"
                           cols="12 6@tablet start-1" />
        </hm-card-inner>
      </card>

      <card id="policiestaxes" :title="$t('admin.pageRatePlan.card.policiesTaxes')" cols="12 6@desktop">
        <hm-card-inner>
          <card-badge :tooltip="$t('betaVersion.tooltip')">{{ $t('betaVersion.title') }}</card-badge>
          <hm-input-select v-model="ratePlan.policyId"
                           :disabled="ratePlan.deleted"
                           :label="$t('admin.pageRatePlan.policyId')"
                           no-select-label
                           name="policyId"
                           cols="12 6@tablet start-1">
            <option :value="null">{{ $t('admin.pageRatePlan.hotelDefaultPolicy') }}</option>
            <template v-for="policy in hotelSettings.policies.all(ratePlan.policyId)">
              <option :value="policy.id" :key="policy.id">{{ policy.name }}</option>
            </template>
            <template slot="icon-inline-right" slot-scope="slot">
              <input-icon-button :border="true"
                                 :disabled="(isEmpty(ratePlan.policyId) && isEmpty(hotelSettings.details.defaultPolicyId)) || ratePlan.deleted"
                                 icon="edit"
                                 @click="$router.push({ name: 'policy', params: { policyId: ratePlan.policyId || hotelSettings.details.defaultPolicyId }})"/>
            </template>
          </hm-input-select>
        </hm-card-inner>
        <hm-card-inner>
          <template v-if="isEmpty(ratePlan.policyId) && isEmpty(hotelSettings.details.defaultPolicyId)">
            <hm-input-money v-model="ratePlan.webPrepayment"
                            :lazy="true"
                            :disabled="ratePlan.deleted"
                            :label="$t('admin.pageRatePlan.webPrepayment')"
                            name="webPrepayment"
                            currency="%"
                            cols="12 6@tablet start-1" />
            <hm-input-select v-if="ratePlan.type === 'DayRate'"
                             v-model="ratePlan.guaranteePolicyType"
                             :disabled="ratePlan.deleted"
                             :label="$t('admin.pageRatePlan.guaranteePolicyType')"
                             no-select
                             cols="12 6@tablet start-1"
                             name="guaranteePolicyType"
                             enum-name="GuaranteePolicyType" />
          </template>

          <hm-multiselect id="applicableTaxes"
                          v-model="ratePlan.applicableTaxes"
                          :options="hotelSettings.taxes.all(ratePlan.applicableTaxes).filter(it => !it.deleted)"
                          :disabled="ratePlan.deleted"
                          :label="$t('admin.pageRatePlan.applicableTaxes')"
                          cols="12 6@tablet start-1"
                          name="applicableTaxes"
                          label-expression="${option.name[$shortLocale]}" />

          <hm-input-money v-if="ratePlan.type === 'Package'"
                          v-model="ratePlan.fromPrice"
                          :disabled="ratePlan.deleted"
                          :currency="hotelSettings.details.currency"
                          :label="$t('admin.pageRatePlan.fromPrice')"
                          :help="$t('admin.pageRatePlan.fromPrice_help')"
                          cols="12 6@tablet start-1" />
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-select id="type"
                           v-model="ratePlan.fiscalizationChargeType"
                           :disabled="ratePlan.deleted || !hasRole('ROLE_SYSADMIN')"
                           :label="$t('admin.pageRatePlan.fiscalizationChargeType')"
                           name="type"
                           enum-name="FiscalizationChargeType"
                           cols="12 6@tablet start-1" />

          <hm-input v-if="hasAnyLicense(['LedgerReport', 'DatevReport'])"
                    id="ledgerCode"
                    v-model="ratePlan.ledgerCode"
                    :disabled="ratePlan.deleted"
                    :label="$t('admin.pageRatePlan.ledgerCode')"
                    cols="12 6@tablet start-1"
                    name="ledgerCode" />

          <hm-input v-if="hasLicense('FiskaltrustAgencyBusiness')"
                    id="fiscalizationAgencyId"
                    v-model="ratePlan.fiscalizationAgencyId"
                    :disabled="ratePlan.deleted || !hasRole('ROLE_SYSADMIN')"
                    :label="$t('admin.pageRatePlan.fiscalizationAgencyId')"
                    cols="12 6@tablet start-1"
                    name="fiscalizationAgencyId" />
        </hm-card-inner>
      </card>
      <template v-if="ratePlan.rateEntryMode !== 'Daily'">
        <card id="childrates" :title="$t('admin.pageRatePlan.card.childRates')" cols="12 6@desktop">
          <hm-card-inner v-if="!isEmpty(hotelSettings.ageCategories.all())" >
            <hm-input-toggle v-model="ratePlan.useRatePlanLevelChildRates"
                             :disabled="ratePlan.deleted"
                             :label="$t('admin.pageRatePlan.useRatePlanLevelChildRates')"
                             :help="$t('admin.pageRatePlan.useRatePlanLevelChildRates_help')"
                             name="useRatePlanLevelChildRates"
                             cols="12" />
            <template v-if="ratePlan.useRatePlanLevelChildRates">
              <template v-for="(ageCategory, acIdx) in hotelSettings.ageCategories.all()">
                <hm-input-rate v-if="ageCategory.ageFrom >= 0 && ageCategory.deleted === false"
                               v-model="ratePlan.childRates[ageCategory.id]"
                               :disabled="ratePlan.deleted"
                               :lazy="true"
                               :rate-mode="false"
                               :relative="true"
                               :absolute="ratePlan.ratePlanMode !== 'Derived'"
                               :label="`${ageCategory.name[$shortLocale]} (${ageCategory.ageFrom}-${ageCategory.ageUntil})`"
                               :key="ageCategory.id"
                               :cols="`12 6@tablet ${acIdx === 1 ? 'start-1' : ''}`" />
              </template>
            </template>
            <hint v-else :text="$t('pageRatePlan.hintSeasonalChildRates')" />
          </hm-card-inner>
        </card>
      </template>

      <card v-if="!isNil(ratePlan.includedInHouseServices) && ratePlan.includedInHouseServices.length > 0" :title="$t('admin.pageRatePlan.card.includedAdditionalServices')" cols="12">
        <hm-card-inner v-for="(ihs, idx) in ratePlan.includedInHouseServices"
                       :key="idx"
                       :deletable="true"
                       :title="$t('admin.pageRatePlan.card.additionalService', { idx: idx+1 })"
                       @delete="deleteIncludedIhs(ihs)">
          <hm-input-select v-model="ihs.inHouseServiceId"
                           :disabled="ratePlan.deleted"
                           :label="$t('admin.pageRatePlan.inHouseServiceId')"
                           name="inHouseServiceId"
                           cols="12 4@tablet 3@desktop">
            <optgroup v-for="group in includedAdditionalServices" :label="group.group">
              <option v-for="item in group.options" :value="item.id" :disabled="item.disabled" v-html="item.name"></option>
            </optgroup>
          </hm-input-select>
          <template v-if="!isEmpty(ihs) && !isEmpty(ihs.inHouseServiceId)">
            <hm-input-number v-model="ihs.count"
                             :disabled="ratePlan.deleted"
                             :label="$t('admin.pageRatePlan.count')"
                             name="count"
                             validate="required"
                             cols="12 4@tablet 3@desktop" />
            <hm-input-money :lazy="true"
                            :value="isEmpty(ihs.inHouseServiceId) ? null : findHotelSettingById('inHouseServices', ihs.inHouseServiceId).amount"
                            :disabled="true"
                            :label="$t('admin.pageRatePlan.amount')"
                            name="amount"
                            cols="12 4@tablet 3@desktop">
              <template slot="icon-inline-right">
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerRoom'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-room" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerPerson'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-person" />
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerStay'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-stay" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerNight'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-night" />
              </template>
            </hm-input-money>
            <hm-input-money :lazy="true"
                            v-model="ihs.amountOverride"
                            :disabled="ratePlan.deleted"
                            :label="$t('admin.pageRatePlan.amountOverride')"
                            name="amountOverride"
                            cols="12 4@tablet 3@desktop" />
            <hm-input-toggle v-model="ihs.applyRatePlanTaxes"
                             :disabled="ratePlan.deleted"
                             :label="$t('admin.pageRatePlan.applyRatePlanTaxes')"
                             :help="$t('admin.pageRatePlan.applyRatePlanTaxes_help')"
                             cols="12 4@tablet 3@desktop" />
                             <!--
            <hm-input-toggle v-model="ihs.includeForPrepaymentCalculation" :disabled="ratePlan.deleted" label="Berücksichtige für Anzahlungsberechnung?" cols="12 4@tablet 3@desktop" />
            -->
          </template>
        </hm-card-inner>
      </card>
      <card v-if="!isNil(ratePlan.mandatoryInHouseServices) && ratePlan.mandatoryInHouseServices.length > 0" :title="$t('admin.pageRatePlan.card.mandatoryAdditionalServices')" cols="12">
        <hm-card-inner v-for="(ihs, idx) in ratePlan.mandatoryInHouseServices"
                       :key="idx"
                       :deletable="!ratePlan.google || hasRole('ROLE_SYSADMIN')"
                       :title="$t('admin.pageRatePlan.card.additionalService', { idx: idx+1 })"
                       @delete="deleteMandatoryIhs(ihs)">
          <hm-input-select v-model="ihs.inHouseServiceId"
                           :disabled="ratePlan.deleted"
                           :label="$t('admin.pageRatePlan.inHouseServiceId')"
                           name="inHouseServiceId"
                           cols="12 4@tablet 3@desktop">
            <optgroup v-for="group in additionalServices" :label="group.group">
              <option v-for="item in group.options"
                      v-if="!isNil(item.amount)"
                      :value="item.id"
                      :disabled="item.disabled"
                      v-html="item.name"></option>
            </optgroup>
          </hm-input-select>
          <template v-if="!isEmpty(ihs) && !isEmpty(ihs.inHouseServiceId)">
            <hm-input-number v-model="ihs.count"
                             :disabled="ratePlan.deleted || (ratePlan.google && !hasRole('ROLE_SYSADMIN'))"
                             :label="$t('admin.pageRatePlan.count')"
                             name="count"
                             validate="required"
                             cols="12 4@tablet 3@desktop" />
            <hm-input-money :lazy="true"
                            :value="isEmpty(ihs.inHouseServiceId) ? null : findHotelSettingById('inHouseServices', ihs.inHouseServiceId).amount"
                            :disabled="true"
                            :label="$t('admin.pageRatePlan.amount')"
                            name="amount"
                            cols="12 4@tablet 3@desktop">
              <template slot="icon-inline-right">
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerRoom'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-room" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerPerson'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-person" />
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerStay'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-stay" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerNight'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-night" />
              </template>
            </hm-input-money>
            <hm-input-money :lazy="true"
                            v-model="ihs.amountOverride"
                            :disabled="ratePlan.deleted"
                            :label="$t('admin.pageRatePlan.amountOverride')"
                            name="amountOverride"
                            cols="12 4@tablet 3@desktop" />
            <hm-input-toggle v-model="ihs.applyRatePlanTaxes"
                             :disabled="ratePlan.deleted"
                             :label="$t('admin.pageRatePlan.applyRatePlanTaxes')"
                             :help="$t('admin.pageRatePlan.applyRatePlanTaxes_help')"
                             cols="12 4@tablet 3@desktop" />
            <hm-input-toggle v-model="ihs.includeForPrepaymentCalculation" :disabled="ratePlan.deleted" :label="$t('admin.pageRatePlan.includeForPrepaymentCalculation')" cols="12 4@tablet 3@desktop" />
          </template>
        </hm-card-inner>
      </card>

      <card v-if="!isNil(ratePlan.bookableInHouseServices) && ratePlan.bookableInHouseServices.length > 0" :title="$t('admin.pageRatePlan.card.bookableAdditionalServices')" cols="12">
        <hm-card-inner v-for="(ihs, idx) in ratePlan.bookableInHouseServices"
                       :key="idx"
                       :deletable="true"
                       :title="$t('admin.pageRatePlan.card.bookableAdditionalService', { idx: idx+1 })"
                       @delete="deleteBookableIhs(ihs)">
          <hm-input-select v-model="ihs.inHouseServiceId"
                           :disabled="ratePlan.deleted"
                           :label="$t('admin.pageRatePlan.inHouseServiceId')"
                           name="inHouseServiceId"
                           cols="12 4@tablet 3@desktop">
            <optgroup v-for="group in additionalServices" :label="group.group">
              <option v-for="item in group.options"
                      v-if="!isNil(item.amount)"
                      :value="item.id"
                      :disabled="item.disabled"
                      v-html="item.name"></option>
            </optgroup>
          </hm-input-select>
          <template v-if="!isEmpty(ihs) && !isEmpty(ihs.inHouseServiceId)">
            <hm-input-number v-model="ihs.minCount"
                             :disabled="ratePlan.deleted"
                             :label="$t('admin.pageRatePlan.minCount')"
                             :validate="`required|max_value:100`"
                             name="minCount"
                             cols="12 4@tablet 3@desktop" />
            <hm-input-number v-model="ihs.maxCount"
                             :disabled="ratePlan.deleted"
                             :label="$t('admin.pageRatePlan.maxCount')"
                             :validate="`required|max_value:100`"
                             name="maxCount"
                             cols="12 4@tablet 3@desktop" />
            <hm-input-money :value="isEmpty(ihs.inHouseServiceId) ? null : findHotelSettingById('inHouseServices', ihs.inHouseServiceId).amount"
                            :lazy="true"
                            :disabled="true"
                            :label="$t('admin.pageRatePlan.amount')"
                            name="amount"
                            cols="12 4@tablet 3@desktop">
              <template slot="icon-inline-right">
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerRoom'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-room" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode === 'PerPerson'"
                                   :border="true"
                                   :title="$t(`RateMode.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateMode}`)"
                                   :disabled="true"
                                   icon="per-person" />
                <input-icon-button v-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerStay'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-stay" />
                <input-icon-button v-else-if="findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency === 'PerNight'"
                                   :border="true"
                                   :title="$t(`RateFrequency.${findHotelSettingById('inHouseServices', ihs.inHouseServiceId).rateFrequency}`)"
                                   :disabled="true"
                                   icon="per-night" />
              </template>
            </hm-input-money>
            <hm-input-toggle v-model="ihs.includeForPrepaymentCalculation" :disabled="ratePlan.deleted" :label="$t('admin.pageRatePlan.includeForPrepaymentCalculation')" cols="12 4@tablet 3@desktop" />
          </template>
        </hm-card-inner>
      </card>

      <card v-if="ratePlan.type === 'Package' && ratePlan.rateEntryMode !== 'Daily' && !isEmpty(ratePlan.validityDateRanges)" :title="$t('admin.pageRatePlan.card.validityDateRanges')" cols="12">
        <hm-card-inner v-for="dateRange in ratePlan.validityDateRanges" :key="dateRange.id" :deletable="true" @delete="deleteValidityDateRange(dateRange)">
          <hm-input-date v-model="dateRange.startDate"
                         :required="false"
                         :label="$t('admin.pageRatePlan.startDate')"
                         cols="12 4@tablet 3@desktop"
                         name="startDate" />
          <hm-input-date v-model="dateRange.endDate"
                         :min-date="dateRange.startDate"
                         :max-days="3650"
                         :required="false"
                         :label="$t('admin.pageRatePlan.endDate')"
                         cols="12 4@tablet 3@desktop"
                         name="endDate" />
        </hm-card-inner>
      </card>

      <ai-text-generator />

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <hm-floating-button v-if="!ratePlan.deleted && canDelete"
                            id="deleteRate"
                            :label="$t('admin.pageRatePlan.actions.deleteRate')"
                            icon="delete"
                            color="error"
                            @click="deleteRatePlan(false)" />
        <hm-floating-button v-if="!ratePlan.deleted"
                            id="aiText"
                            label="AI Text"
                            icon="magic-wand"
                            color="blue"
                            @click="clickGenerateDescription"/>
        <hm-floating-button v-if="!ratePlan.deleted" :label="$t('admin.pageRatePlan.actions.more')" icon="plus" color="blue">
          <hm-floating-child-button v-if="ratePlan.type === 'DayRate' && hasLicense('RelativeRates') && canAddChildRatePlan"
                                    id="addDerivedRate"
                                    :label="$t('admin.pageRatePlan.actions.addDerivedRate')"
                                    icon="plus"
                                    color="blue"
                                    @click="addRatePlan()" />
          <hm-floating-child-button v-if="ratePlan.type === 'Package' && hasLicense('RelativePackageRates') && canAddChildRatePlan"
                                    id="addDerivedPackage"
                                    :label="$t('admin.pageRatePlan.actions.addDerivedPackage')"
                                    icon="plus"
                                    color="blue"
                                    @click="addRatePlan()" />
          <hm-floating-child-button v-if="ratePlan.type === 'Package' && ratePlan.rateEntryMode !== 'Daily'"
                                    id="addValidityPeriod"
                                    :label="$t('admin.pageRatePlan.actions.addValidityPeriod')"
                                    icon="plus"
                                    color="blue"
                                    @click="addValidityDateRange()" />
          <hm-floating-child-button v-if="hasLicense('OnlineBookingAdditionalServices')"
                                    id="addBookableAdditionalService"
                                    :label="$t('admin.pageRatePlan.actions.addBookableAdditionalService')"
                                    icon="plus"
                                    color="blue"
                                    @click="addBookableIhs()" />
          <hm-floating-child-button v-if="hasLicense('AdditionalServices')"
                                    id="addMandatoryInHouseService"
                                    :label="$t('admin.pageRatePlan.actions.addMandatoryInHouseService')"
                                    :disabled="ratePlan.google && !hasRole('ROLE_SYSADMIN')"
                                    icon="plus"
                                    color="blue"
                                    @click="addMandatoryIhs()" />
          <hm-floating-child-button v-if="hasLicense('AdditionalServices')"
                                    id="addIncludedAdditionalService"
                                    :label="$t('admin.pageRatePlan.actions.addIncludedAdditionalService')"
                                    icon="plus"
                                    color="blue"
                                    @click="addIncludedIhs()" />
        </hm-floating-button>

        <hm-floating-button v-if="ratePlan.deleted && hasRole('ROLE_SYSADMIN')"
                            id="delete"
                            :label="$t('admin.pageRatePlan.actions.undeleteRate')"
                            icon="delete"
                            color="error"
                            @click="undeleteRatePlan()" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import uuid from 'uuid';
import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, flattenDeep, groupBy, isEmpty, isNil, keyBy, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';
import HmCardInnerInner from '../../lib/layout/HmCardInnerInner';
import HmCardInnerLine from '../../lib/layout/HmCardInnerLine';
import HmTable from '../../lib/table/HmTable';
import HmInputToggle from '../../lib/inputs/HmInputToggle.vue';
import AiTextGenerator from '../../lib/util/AiTextGenerator.vue';
import HmInputSelect from '../../lib/inputs/HmInputSelect.vue';
import Card from '../../lib/layout/Card.vue';
import HmCol from '../../lib/layout/HmCol.vue';

export default {
  name: 'PageRatePlan',
  components: { HmCol, Card, HmInputSelect, AiTextGenerator, HmInputToggle, HmTable, HmCardInnerLine, HmCardInnerInner, HmMultiselect, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'ratePlan',
      variable: 'ratePlan',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.ratePlan.deleted === true) return;

        const toSave = cloneDeep(this.ratePlan);

        toSave.includedInHouseServices = toSave.includedInHouseServices.filter(it => !isEmpty(it.inHouseServiceId));
        toSave.mandatoryInHouseServices = toSave.mandatoryInHouseServices.filter(it => !isEmpty(it.inHouseServiceId));
        toSave.bookableInHouseServices = isNil(toSave.bookableInHouseServices) ? [] : toSave.bookableInHouseServices.filter(it => !isEmpty(it.inHouseServiceId));

        this.axios.put(this.serviceUrls.ratePlan.get(this, this.ratePlanId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();

            // to make sure any changes to parentId is properly reflected in the navigation
            if (this.ratePlan.ratePlanMode === 'Derived') {
              this.$serviceBus.$emit('ratePlan.list');
            } else {
              // and name changes as well
              this.$serviceBus.$emit('ratePlan.list');
            }
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      ratePlan: null,
      ratePlanId: this.$route.params.ratePlanId,
      ratePlans: [],
    };
  },

  computed: {
    parentRatePlans: function () {
      let ret = this.ratePlans;

      const findChildren = function (id) {
        const toDelete = [];
        return ret.map((it) => {
          if (it.id === id) {
            return [it.id];
          } else if (it.parentRatePlanId === id) {
            return findChildren(it.id);
          } else {
            return [];
          }
        });
      };

      const toDelete = flattenDeep(findChildren(this.ratePlan.id));

      ret = this.hotelSettings.ratePlans.filter(it => !toDelete.includes(it.id));

      return ret;
    },
    canDelete: function () {
      return this.hotelSettings.ratePlans.filter(it => !it.deleted && it.parentRatePlanId === this.ratePlan.id).length === 0;
    },
    canAddChildRatePlan: function () {
      if (!this.hasRole('ROLE_SYSADMIN') && this.ratePlan.rateEntryMode === 'Daily') {
        return this.ratePlanLevel === 0;
      }
      return !this.ratePlan.deleted && this.ratePlanLevel < this.hotelSettings.details.maxNestedRelativeRateLevels;
    },
    ratePlanLevel: function () {
      if (isNil(this.ratePlan)) return 0;
      if (this.ratePlan.ratePlanMode === 'Regular') return 0;
      else {
        let itRp = this.findHotelSettingById('ratePlans', this.ratePlan.id);
        let cnt = 0;
        for (;!isNil(itRp); cnt++) {
          if (!isEmpty(itRp.parentRatePlanId)) {
            itRp = this.findHotelSettingById('ratePlans', itRp.parentRatePlanId);
          } else {
            itRp = null;
          }
        }
        return cnt - 1;
      }
    },
    restrictions: function () {
      return this.$store.getters['enums/enums'].Restrictions
        .filter(it => !['CancellationPolicy', 'PrepaymentPolicy', 'ClosedArrival', 'ClosedDeparture'].includes(it))
        .filter(it => this.hasLicense('LeadTimeToBook') || !['MinimumLeadTimeToBook', 'MaximumLeadTimeToBook'].includes(it))
        .map(it => ({
          id: it,
          name: this.$t(`Restrictions.${it}`),
        }));
      // inheritRestrictions
    },
    additionalServices: function () {
      return [
        ...sortBy(
          Object.entries(groupBy(this.hotelSettings.inHouseServices.all().filter(it => it.level === 'RatePlan'), it => it.type))
            .map(it => ({
              group: this.$t(`InHouseServiceType.${it[0]}`),
              options: sortBy(it[1].map((ihs) => {
                const ret = {
                  id: ihs.id,
                  name: ihs.name[this.$shortLocale],
                  type: 'InHouseService',
                  amount: ihs.amount,
                  disabled: this.includedMandatoryIhsIds.includes(ihs.id) || ihs.deleted,
                };

                if (ihs.rateFrequency === 'PerNight') {
                  ret.name = `${ret.name} (${this.$t('RateFrequency.PerNight')})`;
                }

                return ret;
              }), ihs => ihs.name),
            })),
          it => it.group,
        ),
      ];
    },
    includedMandatoryIhsIds: function () {
      return isNil(this.ratePlan.mandatoryInHouseServices) ? [] : this.ratePlan.mandatoryInHouseServices.map(it => it.inHouseServiceId);
    },
    includedAdditionalServices: function () {
      return [
        ...sortBy(
          Object.entries(groupBy(this.hotelSettings.inHouseServices.all().filter(it => it.level === 'RatePlan'), it => it.type))
            .map(it => ({
              group: this.$t(`InHouseServiceType.${it[0]}`),
              options: sortBy(it[1].map((ihs) => {
                const ret = {
                  id: ihs.id,
                  name: ihs.name[this.$shortLocale],
                  type: 'InHouseService',
                  amount: ihs.amount,
                  disabled: this.includedMandatoryIhsIds.includes(ihs.id) || ihs.deleted,
                };

                if (ihs.rateFrequency === 'PerNight') {
                  ret.name = `${ret.name} (${this.$t('RateFrequency.PerNight')})`;
                }

                return ret;
              }), ihs => ihs.name),
            })),
          it => it.group,
        ),
      ];
    },
    includedIncludedIhsIds: function () {
      return isNil(this.ratePlan.includedInHouseServices) ? [] : this.ratePlan.includedInHouseServices.map(it => it.inHouseServiceId);
    },
  },

  watch: {
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    isEmpty,
    loadData() {
      this.axios.get(this.serviceUrls.ratePlan.get(this, this.ratePlanId))
        .then((response) => {
          if (isNil(this.ratePlan)) {
            this.ratePlan = response.data.data;
          }
        });
      this.axios.get(this.serviceUrls.ratePlan.list(this))
        .then((response) => {
          this.ratePlans = response.data.data;
        });
    },
    addBookableIhs() {
      this.ratePlan.bookableInHouseServices.push({
        minCount: 1,
        maxCount: 1,
        includeForPrepaymentCalculation: false,
        inHouseServiceId: null,
        hotelId: this.hotelSettings.hotel.id,
      });
    },
    deleteBookableIhs(ihs) {
      const idx = this.ratePlan.bookableInHouseServices.indexOf(ihs);
      if (idx >= 0) {
        this.ratePlan.bookableInHouseServices.splice(idx, 1);
      }
    },
    deleteMandatoryIhs(ihs) {
      const idx = this.ratePlan.mandatoryInHouseServices.indexOf(ihs);
      if (idx >= 0) {
        this.ratePlan.mandatoryInHouseServices.splice(idx, 1);
      }
    },
    addMandatoryIhs() {
      this.ratePlan.mandatoryInHouseServices.push({
        count: 0,
        applyRatePlanTaxes: false,
        includeForPrepaymentCalculation: false,
        amountOverride: null,
        inHouseServiceId: null,
        hotelId: this.hotelSettings.hotel.id,
      });
    },
    addValidityDateRange() {
      if (this.ratePlan.validityDateRanges == null) {
        this.ratePlan.validityDateRanges = [];
      }
      this.ratePlan.validityDateRanges.push({
        id: uuid.v4(),
        startDate: null,
        endDate: null,
      });
    },
    deleteValidityDateRange(dateRange) {
      const idx = this.ratePlan.validityDateRanges.indexOf(dateRange);
      if (idx >= 0) {
        this.ratePlan.validityDateRanges.splice(idx, 1);
      }
    },
    deleteIncludedIhs(ihs) {
      const idx = this.ratePlan.includedInHouseServices.indexOf(ihs);
      if (idx >= 0) {
        this.ratePlan.includedInHouseServices.splice(idx, 1);
      }
    },
    addIncludedIhs() {
      this.ratePlan.includedInHouseServices.push({
        count: 0,
        applyRatePlanTaxes: false,
        includeForPrepaymentCalculation: false,
        amountOverride: null,
        inHouseServiceId: null,
        hotelId: this.hotelSettings.hotel.id,
      });
    },
    deleteRatePlan(confirmed = false) {
      if (this.canDelete) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.ratePlan.get(this, this.ratePlanId))
            .then((response) => {
              this.ratePlan = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('ratePlan.list');
              this.$router.push({
                name: 'ratePlans',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showConfirmModal(
            this.$t('pageRatePlan.confirmDeleteRatePlan'),
            (doNotShowAgain) => {
              this.deleteRatePlan(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('pageRatePlan.deleteFailedDeleteDerivedRatePlansFirst'));
      }
    },
    addRatePlan() {
      const type = this.ratePlan.type;
      this.axios.post(this.serviceUrls.ratePlan.list(this), {
        name: {
          [this.hotelSettings.details.primaryLanguage]: `${type === 'DayRate' ? this.$t('admin.pageRatePlans.newRatePlanName') : this.$t('admin.pageRatePlans.newPackageName')}`,
        },
        description: {
          [this.hotelSettings.details.primaryLanguage]: '',
        },
        type: isEmpty(type) ? 'DayRate' : type,
        ratePlanMode: 'Derived',
        publicVisible: false,
        parentRatePlanId: this.ratePlan.id,
      })
        .then((response) => {
          this.$serviceBus.$emit('hotelSettings.update');
          this.$serviceBus.$emit('ratePlan.list');
        });
    },
    undeleteRatePlan(bool1) {
      const toSave = cloneDeep(this.ratePlan);

      toSave.includedInHouseServices = toSave.includedInHouseServices.filter(it => !isEmpty(it.inHouseServiceId));
      toSave.mandatoryInHouseServices = toSave.mandatoryInHouseServices.filter(it => !isEmpty(it.inHouseServiceId));
      toSave.deleted = false;

      this.axios.patch(this.serviceUrls.ratePlan.get(this, this.ratePlanId), toSave)
        .then((response) => {
          this.showMessages(response);
          this.ratePlan = response.data.data;

          this.$serviceBus.$emit('ratePlan.list');
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    clickGenerateDescription() {
      this.$serviceBus.$emit('aiTextGenerator.show', this.serviceUrls.aiTextGenerator.ratePlanDescription(this, this.ratePlanId));
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageRatePlan.vue